import React, { useState, useRef, useEffect } from 'react'
import { Classes, Dialog } from "@blueprintjs/core"
import { observer } from "mobx-react"
import { PlanrButton } from 'modules/common/components/planr/button/Button'
import { Buttons } from "modules/common/components/form"
import { FileLink } from "modules/common/components/files/FileLink"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { LocalLoader } from "modules/common/components/loader/Loader"
import { PendingSignatureItemType } from 'modules/main/models/order-content-task-signatures'
import { useCryptoPro } from './useCryptoPro'
import { eat } from "modules/common/services/typescript"
import styles from "./CryptoDialog.module.scss"
import { PlanRDndUploader } from 'modules/common/components/planr/dndUploader/FileDndUploader'

interface AttachSignatureDialogProps {
  isOpen: boolean
  selectedSignature: PendingSignatureItemType | null
  selectedSignerName: string | null
  onClose: () => void
  onAttachSignature: (signature: PendingSignatureItemType, signatureData: string) => Promise<void>
  onNotifyError: (error: string) => void
  onNotifySuccess?: (message: string) => void
  baseUrl: string
}

export const AttachSignatureDialog: React.FC<AttachSignatureDialogProps> = observer(({
  isOpen,
  selectedSignature,
  selectedSignerName,
  onClose,
  onAttachSignature,
  onNotifyError,
  onNotifySuccess,
  baseUrl,
}) => {
  const { verifyFileHash, fetchCerts, } = useCryptoPro()
  const [selectedSigFile, setSelectedSigFile] = useState<File | null>(null)
  const [selectedResultFile, setSelectedResultFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [hashMatch, setHashMatch] = useState(false)

  const resultFileInputRef = useRef<HTMLInputElement>(null)
  const sigFileInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (isOpen) {
      fetchCerts()
    }
  }, [isOpen, fetchCerts])

  // Обработчик выбора файла результата
  const handleResultFileSelect = async (file: File) => {
    setSelectedResultFile(file)
    setVerificationStatus('idle')
    setHashMatch(false)

    // Автоматически запускаем проверку хэша
    if (selectedSignature) {
      try {
        setIsVerifying(true)

        const isValid = await verifyFileHash(
          file,
          selectedSignature.fileId,
          selectedSignature.fileName,
          baseUrl
        )

        if (isValid) {
          setVerificationStatus('success')
          setHashMatch(true)
          onNotifySuccess?.('Хэш файла совпадает с файлом в базе. Можно прикреплять подпись.')
        } else {
          setVerificationStatus('error')
          setHashMatch(false)
          onNotifyError('Хэш загруженного файла НЕ совпадает с файлом в базе! Подпись не будет принята.')
        }
      } catch (error) {
        setVerificationStatus('error')
        onNotifyError(`Ошибка верификации: ${(error as any).message}`)
      } finally {
        setIsVerifying(false)
      }
    }
  }



  // Обработчик выбора файла подписи
  const handleSigFileSelect = async (file: File) => {
    if (file) {
      if (file.name.endsWith('.sig')) {
        setSelectedSigFile(file)
      } else {
        onNotifyError('Пожалуйста, выберите файл с расширением .sig')
        if (sigFileInputRef.current) {
          sigFileInputRef.current.value = ''
        }
      }
    }
  }



  // Прикрепление подписи
  const handleAttach = async () => {
    if (!selectedSignature || !selectedSigFile) {
      onNotifyError('Необходимо выбрать файл подписи (.sig)')
      return
    }

    if (!hashMatch) {
      onNotifyError('Сначала проверьте хэш файла результата. Он должен совпадать с файлом в базе.')
      return
    }

    try {
      setIsUploading(true)

      const reader = new FileReader()
      reader.onload = async (e) => {
        const base64Data = (e.target?.result as string).split(',')[1]

        await onAttachSignature(selectedSignature, base64Data)
        onClose()
        resetForm()
        onNotifySuccess?.('Подпись успешно прикреплена')
      }
      reader.readAsDataURL(selectedSigFile)
    } catch (error) {
      onNotifyError(`Ошибка при загрузке подписи: ${(error as any).message}`)
    } finally {
      setIsUploading(false)
    }
  }

  const resetForm = () => {
    setSelectedSigFile(null)
    setSelectedResultFile(null)
    setVerificationStatus('idle')
    setHashMatch(false)
    if (resultFileInputRef.current) resultFileInputRef.current.value = ''
    if (sigFileInputRef.current) sigFileInputRef.current.value = ''
  }

  const handleCancel = () => {
    resetForm()
    onClose()
  }

  if (!selectedSignature) return null

  const canAttach = selectedSigFile && hashMatch

  return (
    <Dialog
      title="Прикрепление подписи для аутсорсера"
      isOpen={isOpen}
      onClose={handleCancel}
      backdropClassName="standard"
      className={`${styles.smallDialog} ${styles.attachDialog}`}
    >
      <div className={Classes.DIALOG_BODY}>
        <div className={styles.warningBox}>
          <GeneralIcon
            type="general-warning"
            style={{
              color: "#fbb360",
              fontSize: "20px",
              marginRight: "12px",
              flexShrink: 0
            }}
          />
          <div className={styles.warningText}>
            <strong>Внимание!</strong> Вы пытаетесь загрузить файл подписи аутсорсера <strong>{selectedSignerName}</strong>. Слияние отдельных файлов подписи невозможно, все существующие подписи для текущего файла будут сброшены!<br />
            Для проверки соответствия документа необходимо загрузить файл результата от аутсорсера.
          </div>
        </div>

        <div className={styles.attachInfo}>
          <div className={styles.signRow}>
            <span className={styles.label}>Договор:</span>
            <span className={styles.value}>
              {selectedSignature.orderName}
            </span>
          </div>
          <div className={styles.signRow}>
            <span className={styles.label}>Раздел:</span>
            <span className={styles.value}>{selectedSignature.contentName}</span>
          </div>
          <div className={styles.signRow}>
            <span className={styles.label}>Файл в базе:</span>
            <span className={styles.value}>
              <FileLink
                baseUrl={baseUrl}
                file={{
                  fileId: selectedSignature.fileId,
                  fileName: selectedSignature.fileName
                }}
                onRemove={eat}
                readOnly
                style={{
                  whiteSpace: "normal",
                  fontSize: "10px",
                  height: "auto",
                  padding: "8px 12px",
                  borderColor: "#ea561e",
                  color: "#ea561e"
                }}
              />
              <span className={styles.version}> (версия {selectedSignature.version})</span>
            </span>
          </div>
          <div className={styles.signRow}>
            <span className={styles.label}>Роль:</span>
            <span className={styles.value}>{selectedSignature.signerTypeDisplay}</span>
          </div>
        </div>

        {/* Загрузка файла результата для верификации */}
        <div className={styles.fileSection}>
          <div className={styles.fileSectionTitle}>
            <GeneralIcon type="general-document" style={{ marginRight: '8px' }} />
            Файл результата (от аутсорсера) <span style={{ color: '#e31818' }}>*</span>
          </div>
          <div
            className={styles.fileInputArea}
            onClick={() => resultFileInputRef.current?.click()}
          >
            <PlanRDndUploader
              accept="*"
              multiple={false}
              withLoader={true}
              style={{
                border: "2px dashed #ea561e",
                height: "50px",
                width: "100%",
                maxWidth: "100%",
              }}
              onFileSelected={handleResultFileSelect}
            />
          </div>
          {selectedResultFile && (
            <div className={styles.selectedFileInfo}>
              <GeneralIcon type="general-check" style={{ color: "#1dd278", marginRight: "8px" }} />
              <span>{selectedResultFile.name}</span>
              <PlanrButton
                type="neutral"
                size="small"
                onClick={() => {
                  setSelectedResultFile(null)
                  setVerificationStatus('idle')
                  setHashMatch(false)
                  if (resultFileInputRef.current) resultFileInputRef.current.value = ''
                }}
                style={{ marginLeft: 'auto' }}
              >
                Очистить
              </PlanrButton>
            </div>
          )}
          {isVerifying && (
            <div className={styles.verifyingInfo}>
              <LocalLoader active={true} />
              <span style={{ marginLeft: '8px' }}>Проверка хэша файла...</span>
            </div>
          )}
          {verificationStatus === 'success' && (
            <div className={styles.verifySuccess}>
              <GeneralIcon type="general-check" style={{ color: "#1dd278", marginRight: "8px" }} />
              <span>Хэш совпадает. Файл соответствует файлу в базе данных.</span>
            </div>
          )}

          {verificationStatus === 'error' && (
            <div className={styles.verifyError}>
              <GeneralIcon type="general-cross" style={{ color: "#e31818", marginRight: "8px" }} />
              <span>Хэш НЕ совпадает! Файл был изменен. Подпись недействительна.</span>
            </div>
          )}
        </div>

        {/* Загрузка файла подписи */}
        <div className={styles.fileSection}>
          <div className={styles.fileSectionTitle}>
            <GeneralIcon type="general-lock" style={{ marginRight: '8px' }} />
            Файл открепленной подписи (.sig) <span style={{ color: '#e31818' }}>*</span>
          </div>
          <div
            className={styles.fileInputArea}
            onClick={() => sigFileInputRef.current?.click()}
          >
            <PlanRDndUploader
              accept={"*"}
              multiple={false}
              withLoader={true}
              style={{
                border: "2px dashed #ea561e",
                height: "50px",
                maxWidth: "100%",
                width: "100%",
              }}
              onFileSelected={handleSigFileSelect}
            />
            {/* <input
              ref={sigFileInputRef}
              type="file"
              accept=".sig"
              onChange={handleSigFileSelect}
              style={{ display: 'none' }}
            /> */}
          </div>
          {selectedSigFile && (
            <div className={styles.selectedFileInfo}>
              <GeneralIcon type="general-check" style={{ color: "#1dd278", marginRight: "8px" }} />
              <span>{selectedSigFile.name}</span>
              <PlanrButton
                type="neutral"
                size="small"
                onClick={() => {
                  setSelectedSigFile(null)
                  if (sigFileInputRef.current) sigFileInputRef.current.value = ''
                }}
                style={{ marginLeft: 'auto' }}
              >
                Очистить
              </PlanrButton>
            </div>
          )}
        </div>

        {/* <div className={styles.infoBox}>
          <GeneralIcon type="general-info" style={{ marginRight: '8px', flexShrink: 0, color: '#137cbd' }} />
          <span>
            <strong>Порядок действий:</strong><br />
            1. Загрузите файл результата, который подписал аутсорсер<br />
            2. Нажмите "Проверить хэш файла" - система сравнит его с файлом в базе<br />
            3. Если хэши совпадают, загрузите файл подписи .sig<br />
            4. Нажмите "Прикрепить подпись"
          </span>
        </div> */}
      </div>

      <div className={Classes.DIALOG_FOOTER}>
        <Buttons
          left={
            <div className={styles.dialogButtons}>
              <PlanrButton
                size="small"
                type="greenish"
                onClick={handleAttach}
                disabled={!canAttach || isUploading}
              >
                {isUploading ? 'Загрузка...' : 'Прикрепить подпись'}
              </PlanrButton>
              <PlanrButton
                size="small"
                type="graybtn"
                onClick={handleCancel}
                disabled={isUploading}
              >
                Отмена
              </PlanrButton>
            </div>
          }
        />
      </div>
    </Dialog>
  )
})